/**
 * Wallet Firestore Types
 *
 * Type definitions for Firestore documents stored in:
 * - users/{uid}/wallet/summary (balance, totals, payment methods)
 * - users/{uid}/wallet/earnings/{earningId}
 * - users/{uid}/wallet/expenses/{expenseId}
 * - users/{uid}/wallet/usage/{usageId}
 *
 * These types use snake_case to match Firestore conventions.
 * Use the conversion functions to transform between client and Firestore types.
 */

import { Timestamp } from 'firebase/firestore';
import type { Earning, EarningStatus } from './earnings';
import type { Expense, ExpenseStatus } from './expenses';
import type { Transaction, TransactionType, TransactionStatus, TransactionMethod } from './transactions';

// ============================================================================
// Provider Types
// ============================================================================

// Widened 2026-07-06 — keep in lockstep with the copy in
// src/components/GlobalChatSystem/types.ts (see comment there).
export type AIProvider =
  | 'perplexity'
  | 'claude'
  | 'openai'
  | 'gemini'
  | 'grok'
  | 'zai'
  | 'deepseek'
  | 'minimax'
  | 'moonshot'
  | 'xiaomi'
  | 'qwen'
  | 'typesafe'
  | 'openrouter'
  | 'unknown';

// ============================================================================
// Usage Status Type
// ============================================================================

export type UsageStatus = 'successful' | 'failed' | 'paused';

// ============================================================================
// Wallet Summary Types
// ============================================================================

/**
 * Wallet summary document stored in Firestore at users/{uid}/wallet/summary
 * Contains balance, lifetime totals, and payment methods
 *
 * NOTE: OLV balance is the single source of truth for wallet balance.
 * USD balance is now derived at read time from olv_balance * exchange_rate.
 */
export interface WalletSummaryDoc {
  /** Current OLV balance (can be negative) - SINGLE SOURCE OF TRUTH */
  olv_balance: number;
  /**
   * @deprecated USD balance is now derived from olv_balance * exchange_rate at read time.
   * This field is kept for backward compatibility but is no longer written to.
   * Use the derived value from WalletSummaryContext instead.
   */
  usd_balance: number;
  /** Lifetime total OLV earned (tracked at moment-in-time OLV rate) */
  total_olv_earned: number;
  /** Lifetime total OLV spent (tracked at moment-in-time OLV rate) */
  total_olv_spent: number;
  /** Last update timestamp */
  last_updated: Timestamp;
}

/**
 * Wallet summary (client-side type)
 *
 * NOTE: OLV balance is the single source of truth for wallet balance.
 * USD balance is derived at read time from olvBalance * exchange_rate.
 */
export interface WalletSummary {
  /** Current OLV balance (can be negative) - SINGLE SOURCE OF TRUTH */
  olvBalance: number;
  /**
   * Current USD balance - DERIVED from olvBalance * exchange_rate.
   * This value is computed in WalletSummaryContext, not read from storage.
   */
  usdBalance: number;
  /** Lifetime total OLV earned (tracked at moment-in-time OLV rate) */
  totalOlvEarned: number;
  /** Lifetime total OLV spent (tracked at moment-in-time OLV rate) */
  totalOlvSpent: number;
  /** Last update timestamp in epoch milliseconds */
  lastUpdated: number;
}

// ============================================================================
// Firestore Document Types (snake_case for Firestore)
// ============================================================================

/**
 * Earning document stored in Firestore at users/{uid}/wallet_earnings/{earningId}
 */
export interface EarningDoc {
  id: string;
  timestamp: Timestamp;
  tags: string[];
  summary: string;
  source: string;
  member_id: string;
  amount_usd: number;
  amount_olv: number;
  olv_rate_at_time: number;
  model_id?: string;
  provider?: string;
  status: EarningStatus;
  created_at: Timestamp;
}

/**
 * Expense document stored in Firestore at users/{uid}/wallet/summary/expenses/{expenseId}
 * NOTE: Expenses are immutable snapshots - no update logic exists
 */
export interface ExpenseDoc {
  id: string;
  timestamp: Timestamp;
  tags: string[];
  summary: string;
  member_id: string;
  amount_usd: number;
  amount_olv: number;
  olv_rate_at_time: number;
  model_id?: string;
  provider?: string;
  /**
   * Parity plan Phase 0 (2026-08-02): per-model generation breakdown for
   * multi-model turns (server-written by lib/accountExpense.ts). model_id
   * stays the headline model for back-compat.
   */
  generations?: Array<{
    model: string;
    input_tokens: number;
    output_tokens: number;
    reasoning_tokens?: number;
    upstream_cost_usd: number;
    user_cost_usd: number;
    /** Request-side reasoning-effort level (none…max) when the call site stamped it. */
    effort?: string;
  }>;
  /** Turn id join key across expenses ↔ usage_events ↔ usage_log. */
  turn_id?: string;
  /**
   * What produced this expense (`vm-egress`, `prest0n-session`, …). Written on
   * every row by lib/accountExpense.ts since long before this, and dropped here
   * until 2026-09-11 — so the browser could not tell work a person asked for
   * from work the account did on its own. UsageDoc has always carried it.
   */
  source?: string;
  status: ExpenseStatus;
  created_at: Timestamp;
}

/**
 * Usage document stored in Firestore at users/{uid}/wallet_usage/{usageId}
 */
export interface UsageDoc {
  id: string;
  timestamp: Timestamp;
  tags: string[];
  summary: string;
  source: string;
  member_id: string;
  amount_usd: number;
  amount_olv: number;
  olv_rate_at_time: number;
  model_id: string;
  provider: AIProvider;
  status: UsageStatus;
  created_at: Timestamp;
}

/**
 * Transaction document stored in Firestore at users/{uid}/wallet_transactions/{transactionId}
 */
export interface TransactionDoc {
  id: string;
  timestamp: Timestamp;
  type: TransactionType;
  tags: string[];
  summary: string;
  member_id: string;
  amount_usd: number;
  amount_olv: number;
  olv_rate_at_time: number;
  method: TransactionMethod;
  external_ref?: string;
  status: TransactionStatus;
  created_at: Timestamp;
}

// ============================================================================
// Client-side Usage Type
// ============================================================================

/**
 * Usage record (client-side type matching UI columns)
 */
export interface Usage {
  /** Unique 12-digit numerical identifier */
  id: string;
  /** Timestamp in epoch milliseconds */
  timestamp: number;
  /** Tags for categorization */
  tags: string[];
  /** Summary description */
  summary: string;
  /** Source page/feature */
  source: string;
  /** Member ID of the user (just the ID string) */
  memberId: string;
  /** Amount in USD (snapshot at transaction time) */
  amountUsd: number;
  /** Amount in OLV tokens (snapshot at transaction time) */
  amountOlv: number;
  /** OLV exchange rate at time of transaction */
  olvRateAtTime: number;
  /** AI model ID */
  modelId: string;
  /** Provider name */
  provider: AIProvider;
  /** Status */
  status: UsageStatus;
}

/**
 * Input for recording a new usage transaction
 */
export interface RecordUsageInput {
  /** Summary description (max 25 chars) */
  summary: string;
  /** Source page/feature */
  source: string;
  /** Amount in USD */
  amountUsd: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID */
  modelId: string;
  /** Provider name */
  provider: AIProvider;
  /** Initial status (defaults to 'successful') */
  status?: UsageStatus;
}

// ============================================================================
// Conversion Functions: Client -> Firestore
// ============================================================================

/**
 * Convert client Earning to Firestore EarningDoc
 */
export function toEarningDoc(earning: Earning): Omit<EarningDoc, 'created_at'> {
  return {
    id: earning.id,
    timestamp: Timestamp.fromMillis(earning.timestamp),
    tags: earning.tags,
    summary: earning.summary,
    source: earning.source,
    member_id: earning.memberId,
    amount_usd: earning.amountUsd,
    amount_olv: earning.amountOlv,
    olv_rate_at_time: earning.olvRateAtTime,
    model_id: earning.modelId,
    provider: earning.provider,
    status: earning.status,
  };
}

/**
 * Convert client Expense to Firestore ExpenseDoc
 */
export function toExpenseDoc(expense: Expense): Omit<ExpenseDoc, 'created_at'> {
  return {
    id: expense.id,
    timestamp: Timestamp.fromMillis(expense.timestamp),
    tags: expense.tags,
    summary: expense.summary,
    member_id: expense.memberId,
    amount_usd: expense.amountUsd,
    amount_olv: expense.amountOlv,
    olv_rate_at_time: expense.olvRateAtTime,
    model_id: expense.modelId,
    provider: expense.provider,
    status: expense.status,
  };
}

/**
 * Convert client Usage to Firestore UsageDoc
 */
export function toUsageDoc(usage: Usage): Omit<UsageDoc, 'created_at'> {
  return {
    id: usage.id,
    timestamp: Timestamp.fromMillis(usage.timestamp),
    tags: usage.tags,
    summary: usage.summary,
    source: usage.source,
    member_id: usage.memberId,
    amount_usd: usage.amountUsd,
    amount_olv: usage.amountOlv,
    olv_rate_at_time: usage.olvRateAtTime,
    model_id: usage.modelId,
    provider: usage.provider,
    status: usage.status,
  };
}

/**
 * Convert client Transaction to Firestore TransactionDoc
 */
export function toTransactionDoc(tx: Transaction): Omit<TransactionDoc, 'created_at'> {
  return {
    id: tx.id,
    timestamp: Timestamp.fromMillis(tx.timestamp),
    type: tx.type,
    tags: tx.tags,
    summary: tx.summary,
    member_id: tx.memberId,
    amount_usd: tx.amountUsd,
    amount_olv: tx.amountOlv,
    olv_rate_at_time: tx.olvRateAtTime,
    method: tx.method,
    external_ref: tx.externalRef,
    status: tx.status,
  };
}

// ============================================================================
// Conversion Functions: Firestore -> Client
// ============================================================================

/**
 * Convert Firestore EarningDoc to client Earning
 */
export function fromEarningDoc(doc: EarningDoc): Earning {
  return {
    id: doc.id,
    timestamp: doc.timestamp.toMillis(),
    tags: doc.tags || [],
    summary: doc.summary || '',
    source: doc.source || '',
    memberId: doc.member_id || '',
    amountUsd: doc.amount_usd || 0,
    amountOlv: doc.amount_olv || 0,
    olvRateAtTime: doc.olv_rate_at_time || 0.001,
    modelId: doc.model_id,
    provider: doc.provider,
    status: doc.status || 'pending',
    // R18: redemption stamps absorbed_into_trial on the welcome-credit
    // earning it replaces (functions/src/accessCodes.ts) — surface it so the
    // UI can render "replaced by trial credit" instead of a standing +$100.
    ...((doc as { absorbed_into_trial?: unknown }).absorbed_into_trial === true
      ? { absorbedIntoTrial: true }
      : {}),
  };
}

/**
 * Convert Firestore ExpenseDoc to client Expense
 */
export function fromExpenseDoc(doc: ExpenseDoc): Expense {
  return {
    id: doc.id,
    timestamp: doc.timestamp.toMillis(),
    tags: doc.tags || [],
    summary: doc.summary || '',
    memberId: doc.member_id || '',
    amountUsd: doc.amount_usd || 0,
    amountOlv: doc.amount_olv || 0,
    olvRateAtTime: doc.olv_rate_at_time || 0.001,
    modelId: doc.model_id,
    provider: doc.provider,
    ...(Array.isArray(doc.generations) && doc.generations.length > 0
      ? {
        generations: doc.generations
          .filter((g) => g && typeof g.model === 'string' && g.model.length > 0)
          .map((g) => ({
            model: g.model,
            inputTokens: g.input_tokens || 0,
            outputTokens: g.output_tokens || 0,
            ...(typeof g.reasoning_tokens === 'number' && g.reasoning_tokens > 0
              ? { reasoningTokens: g.reasoning_tokens }
              : {}),
            upstreamCostUsd: g.upstream_cost_usd || 0,
            userCostUsd: g.user_cost_usd || 0,
            ...(typeof g.effort === 'string' && g.effort ? { effort: g.effort } : {}),
          })),
      }
      : {}),
    ...(doc.turn_id ? { turnId: doc.turn_id } : {}),
    ...(typeof doc.source === 'string' && doc.source ? { source: doc.source } : {}),
    status: doc.status || 'pending',
  };
}

/**
 * Convert Firestore UsageDoc to client Usage
 */
export function fromUsageDoc(doc: UsageDoc): Usage {
  return {
    id: doc.id,
    timestamp: doc.timestamp.toMillis(),
    tags: doc.tags || [],
    summary: doc.summary || '',
    source: doc.source || '',
    memberId: doc.member_id || '',
    amountUsd: doc.amount_usd || 0,
    amountOlv: doc.amount_olv || 0,
    olvRateAtTime: doc.olv_rate_at_time || 0.001,
    modelId: doc.model_id || '',
    provider: doc.provider || 'claude',
    status: doc.status || 'successful',
  };
}

/**
 * Convert Firestore TransactionDoc to client Transaction
 */
export function fromTransactionDoc(doc: TransactionDoc): Transaction {
  return {
    id: doc.id,
    timestamp: doc.timestamp.toMillis(),
    type: doc.type || 'payment',
    tags: doc.tags || [],
    summary: doc.summary || '',
    memberId: doc.member_id || '',
    amountUsd: doc.amount_usd || 0,
    amountOlv: doc.amount_olv || 0,
    olvRateAtTime: doc.olv_rate_at_time || 0.001,
    method: doc.method || 'other',
    externalRef: doc.external_ref,
    status: doc.status || 'pending',
  };
}

// ============================================================================
// Wallet Summary Conversion Functions
// ============================================================================

/**
 * Convert client WalletSummary to Firestore WalletSummaryDoc
 */
export function toWalletSummaryDoc(summary: WalletSummary): WalletSummaryDoc {
  return {
    olv_balance: summary.olvBalance,
    usd_balance: summary.usdBalance,
    total_olv_earned: summary.totalOlvEarned,
    total_olv_spent: summary.totalOlvSpent,
    last_updated: Timestamp.fromMillis(summary.lastUpdated),
  };
}

/**
 * Convert Firestore WalletSummaryDoc to client WalletSummary
 */
export function fromWalletSummaryDoc(doc: WalletSummaryDoc): WalletSummary {
  return {
    olvBalance: doc.olv_balance || 0,
    usdBalance: doc.usd_balance || 0,
    totalOlvEarned: doc.total_olv_earned || 0,
    totalOlvSpent: doc.total_olv_spent || 0,
    lastUpdated: doc.last_updated?.toMillis?.() || Date.now(),
  };
}

/**
 * Create a default empty wallet summary
 */
export function createDefaultWalletSummary(): WalletSummary {
  return {
    olvBalance: 0,
    usdBalance: 0,
    totalOlvEarned: 0,
    totalOlvSpent: 0,
    lastUpdated: Date.now(),
  };
}

// ============================================================================
// ID Generation
// ============================================================================

/**
 * Generate a unique 12-digit numerical ID for wallet records
 * Format: 6 digits from timestamp + 6 random digits
 */
export function generateWalletItemId(): string {
  const timestampPart = (Date.now() % 1000000).toString().padStart(6, '0');
  const randomPart = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
  return timestampPart + randomPart;
}
